import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Upload, Play, Pause } from 'lucide-react';
import Spinner from './Spinner';
import { useForm } from 'react-hook-form';
import { useUploadAudio } from '../http-hooks/content/useUploadAudio';
const classNames = require('classnames');

function UploadAudio({ show, onHide, onSuccess = () => { }, orgId, integrationId, selectedAudio }) {
    const [audioFile, setAudioFile] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);
    const { register, reset, handleSubmit, formState: { errors } } = useForm();
    
    // Use React Query mutation hook
    const { mutateAsync: uploadAudio, isPending } = useUploadAudio();

    const togglePlayPause = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!audioRef.current) return;
        
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const onUploadAudio = async (data) => {
        if (audioFile === null) {
            return;
        }

        try {
            await uploadAudio({
                org_id: orgId,
                integration_id: integrationId,
                title: data.title,
                file: audioFile,
                content_id: selectedAudio ? selectedAudio.SK.split('#')[1] : undefined
            });

            onSuccess();
            onHide();
        } catch (error) {
            // Error handling is done in the hook
            console.error('Upload failed:', error);
        }
    };

    useEffect(() => {
        if (selectedAudio) {
            reset({
                title: selectedAudio.title
            });
        } else {
            reset({
                title: "",
            });
        }
    }, [selectedAudio, reset]);

    useEffect(() => {
        if (!show) {
            reset({ title: '' });
            setAudioFile(null);
            setIsPlaying(false);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        }
    }, [show, reset]);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {selectedAudio ? 'Edit Audio File' : 'Upload Audio File'}
                    <p className="modal-subtitle mb-0 mt-1" style={{ fontSize: '14px', fontWeight: 'normal', color: 'var(--bs-secondary-color)' }}>
                        {selectedAudio 
                            ? 'Update the audio title or upload a new file' 
                            : 'Upload MP3 files up to 50MB'}
                    </p>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <form>
                    <div className="mb-4">
                        <label className="form-label fw-semibold">
                            Title <span className="text-danger">*</span>
                        </label>
                        <input 
                            type="text" 
                            name="title" 
                            placeholder="Enter audio title"
                            className={classNames("form-control", { "is-invalid": errors.title })} 
                            {...register("title", { required: true, minLength: 3 })} 
                        />
                        {errors.title && (
                            <div className="invalid-feedback">
                                {errors.title.type === "required" && "Title field is required."}
                                {errors.title.type === "minLength" && "Title must be at least 3 characters long."}
                            </div>
                        )}
                    </div>

                    {selectedAudio && (
                        <>
                            <div className="alert alert-info d-flex align-items-start mb-3" role="alert" style={{ fontSize: '14px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="flex-shrink-0 me-2 mt-0" viewBox="0 0 16 16">
                                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                                </svg>
                                <div>
                                    <strong>Uploaded Audio</strong>
                                    <div className="small mt-1">This is a directly uploaded audio file. Studio editing features are not available for uploaded content.</div>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Current File</label>
                                <div className="d-flex align-items-center gap-2 p-3 rounded" style={{ backgroundColor: 'var(--bs-light-bg-subtle)', border: '1px solid var(--bs-border-color)' }}>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                                        onClick={togglePlayPause}
                                    >
                                        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                                        <span>{isPlaying ? 'Pause' : 'Play'}</span>
                                    </button>
                                    <span className="text-muted small">Listen to current audio</span>
                                </div>
                                <audio 
                                    ref={audioRef}
                                    src={selectedAudio.full_audio_url}
                                    onEnded={() => setIsPlaying(false)}
                                    onPause={() => setIsPlaying(false)}
                                    onPlay={() => setIsPlaying(true)}
                                />
                            </div>
                        </>
                    )}

                    <div className="mb-3">
                        <label className="form-label fw-semibold">
                            {selectedAudio ? 'Upload New File' : 'Audio File'} {!selectedAudio && <span className="text-danger">*</span>}
                            {selectedAudio && <span className="text-muted ms-1" style={{ fontSize: '13px', fontWeight: 'normal' }}>(optional)</span>}
                        </label>
                        <div 
                            className="rounded text-center d-flex align-items-center justify-content-center" 
                            style={{ 
                                cursor: 'pointer', 
                                backgroundColor: audioFile ? 'var(--bs-light-bg-subtle)' : 'transparent',
                                padding: '20px 16px',
                                border: '2px dashed var(--bs-border-color)',
                                minHeight: '150px'
                            }}
                        >
                            <input 
                                id="audio-file-input"
                                name="audio-file" 
                                type="file" 
                                accept=".mp3,audio/mpeg" 
                                onChange={(event) => setAudioFile(event.target.files[0])} 
                                style={{ display: 'none' }}
                            />
                            <label 
                                htmlFor="audio-file-input" 
                                className="d-flex flex-column align-items-center justify-content-center m-0 w-100" 
                                style={{ cursor: 'pointer' }}
                            >
                                {audioFile ? (
                                    <>
                                        <div className="text-success" style={{ fontSize: '28px', lineHeight: '1', marginBottom: '8px' }}>✓</div>
                                        <p className="mb-1 fw-semibold text-success small">File selected</p>
                                        <p 
                                            className="mb-1 text-dark small" 
                                            style={{ 
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                maxWidth: '100%'
                                            }}
                                        >
                                            {audioFile.name}
                                        </p>
                                        <p className="mb-2 text-muted" style={{ fontSize: '13px' }}>{(audioFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setAudioFile(null);
                                            }}
                                        >
                                            Remove File
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={36} style={{ marginBottom: '8px', opacity: 0.6 }} />
                                        <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>Drag and drop or click to browse</p>
                                        <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>MP3 files only • Max 50MB</p>
                                    </>
                                )}
                            </label>
                        </div>
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer className="border-top">
                <button 
                    type="button" 
                    className="btn btn-outline-secondary" 
                    onClick={onHide} 
                    disabled={isPending}
                >
                    Cancel
                </button>
                {isPending ? (
                    <button type="button" className="btn btn-primary d-flex align-items-center" disabled>
                <Spinner color="white" />
                        <span className="ms-2">{selectedAudio ? 'Updating...' : 'Uploading...'}</span>
                    </button>
                ) : (
                    <button 
                        onClick={handleSubmit(onUploadAudio)} 
                        type="button" 
                        className="btn btn-primary"
                        disabled={!audioFile && !selectedAudio}
                    >
                        {selectedAudio ? 'Update Audio' : 'Upload Audio'}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default UploadAudio;
